import React, { useCallback, useEffect, useRef, useState } from 'react';
import RefreshCheckApi from '../../communication_system/axios_apis/refresh_check/refresh_check_api';
import { time, status } from './EventAdminPanel';

const inputOrigins = { member: '회원 정보', edited: '직접 변경', selected: '선택', uploaded: '업로드', recent: '이전 선택' };
const selectedInput = (row, key) => `${row.inputSnapshot?.[key] || '-'} · ${inputOrigins[row.inputSource?.[key]] || '출처 미확인'}`;

function MemberImage({ path, label, initial, side }) {
    const [revision, setRevision] = useState(0);
    const [image, setImage] = useState({ loading: true });
    useEffect(() => {
        const controller = new AbortController();
        let expiry;
        setImage({ loading: true });
        const load =
            revision === 0 && initial ? Promise.resolve(initial) : RefreshCheckApi.get(path, { signal: controller.signal }).then((response) => (side ? response.data.data[side] : response.data.data));
        load.then((value) => {
            if (controller.signal.aborted) return;
            if (value.status !== 'ready') {
                setImage({ error: value.status === 'missing' ? '사진 없음' : value.error || '사진을 불러오지 못했어요.' });
                return;
            }
            const url = new URL(value.url);
            if (!['http:', 'https:'].includes(url.protocol) || ![300, null].includes(value.expiresIn)) throw Error('사진 응답을 확인하지 못했어요.');
            setImage({ url: url.href });
            if (value.expiresIn === 300) expiry = setTimeout(() => setImage({ error: '사진 링크가 만료됐어요.' }), 300000);
        }).catch(() => {
            if (!controller.signal.aborted) setImage({ error: '사진을 불러오지 못했어요.' });
        });
        return () => {
            controller.abort();
            clearTimeout(expiry);
        };
    }, [path, label, initial, side, revision]);
    return (
        <figure className="ai-image">
            {image.url ? <img src={image.url} alt={label} onError={() => setImage({ error: '사진을 불러오지 못했어요.' })} /> : <p role="status">{image.loading ? '사진 불러오는 중' : image.error}</p>}
            <figcaption>{label}</figcaption>
            {image.error && (
                <button type="button" onClick={() => setRevision((value) => value + 1)}>
                    사진 다시 불러오기
                </button>
            )}
        </figure>
    );
}

export default function MemberAdminPanel() {
    const [form, setForm] = useState({ searchType: 1, searchWord: '', profileType: 0 });
    const [query, setQuery] = useState({ searchType: 1, searchWord: '', profileType: 0, page: 1, revision: 0 });
    const [members, setMembers] = useState([]);
    const [count, setCount] = useState(0);
    const [member, setMember] = useState(null);
    const [history, setHistory] = useState([]);
    const [comparison, setComparison] = useState(null);
    const [notice, setNotice] = useState('');
    const [detailNotice, setDetailNotice] = useState('');
    const detailController = useRef(null);
    useEffect(() => {
        const controller = new AbortController();
        const { revision, ...parameters } = query;
        setNotice('회원 목록 불러오는 중');
        RefreshCheckApi.post('/adminUser/list/sinor', { ...parameters, size: 10, platform: 2, orderType: 1, orderByType: 5 }, { signal: controller.signal })
            .then((response) => {
                if (controller.signal.aborted) return;
                setMembers(response.data.data);
                setCount(Number(response.data.all_count));
                setNotice('');
            })
            .catch(() => {
                if (!controller.signal.aborted) setNotice('회원 목록을 불러오지 못했어요.');
            });
        return () => controller.abort();
    }, [query]);
    useEffect(() => () => detailController.current?.abort(), []);
    const select = useCallback(async (row) => {
        detailController.current?.abort();
        const controller = new AbortController();
        detailController.current = controller;
        setMember(row);
        setHistory([]);
        setComparison(null);
        setDetailNotice('생성·변경 기록 불러오는 중');
        try {
            const [generations, changed] = await Promise.all([
                RefreshCheckApi.get(`/adminUser/${row.id}/aiProfile/generations`, { signal: controller.signal }),
                RefreshCheckApi.get(`/adminUser/${row.id}/aiProfile/comparison`, { signal: controller.signal }),
            ]);
            if (controller.signal.aborted) return;
            setHistory(generations.data.data);
            setComparison(changed.data.data);
            setDetailNotice('');
        } catch (error) {
            if (!controller.signal.aborted) setDetailNotice(error.response?.data?.error || '생성·변경 기록을 불러오지 못했어요.');
        }
    }, []);
    const close = () => {
        detailController.current?.abort();
        setMember(null);
        setHistory([]);
        setComparison(null);
        setDetailNotice('');
    };
    const pages = Math.max(1, Math.ceil(count / 10));
    return (
        <section>
            <h2>회원 AI 프로필</h2>
            <p>회원 목록에서 생성·변경 기록을 직접 선택해 확인합니다.</p>
            <form
                className="ai-toolbar"
                onSubmit={(event) => {
                    event.preventDefault();
                    close();
                    setQuery({ ...form, page: 1, revision: query.revision + 1 });
                }}
            >
                <label>
                    검색 기준
                    <select value={form.searchType} onChange={(event) => setForm({ ...form, searchType: Number(event.target.value) })}>
                        <option value={1}>이름</option>
                        <option value={2}>닉네임</option>
                        <option value={4}>회원 ID</option>
                    </select>
                </label>
                <label>
                    검색어
                    <input value={form.searchWord} onChange={(event) => setForm({ ...form, searchWord: event.target.value })} />
                </label>
                <label>
                    <input type="checkbox" checked={form.profileType === 5} onChange={(event) => setForm({ ...form, profileType: event.target.checked ? 5 : 0 })} />
                    현재 취미 AI 프로필
                </label>
                <button type="submit">조회</button>
            </form>
            <p role="status">{notice}</p>
            <div className="ai-table">
                <table>
                    <thead>
                        <tr>
                            <th>회원 ID</th>
                            <th>이름</th>
                            <th>닉네임</th>
                            <th>현재 프로필</th>
                            <th>기록</th>
                        </tr>
                    </thead>
                    <tbody>
                        {members.map((row) => (
                            <tr key={row.id}>
                                <td>{row.id}</td>
                                <td>{row.name}</td>
                                <td>{row.nick_name}</td>
                                <td>{row.profile_source_type === 'AI_HOBBY_PROFILE' ? '취미 AI 프로필' : '일반 프로필'}</td>
                                <td>
                                    <button type="button" onClick={() => select(row)}>
                                        생성·변경 기록 보기
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!members.length && !notice && <p>해당 회원이 없어요.</p>}
            </div>
            <div className="ai-actions">
                <button type="button" disabled={query.page <= 1} onClick={() => setQuery({ ...query, page: query.page - 1 })}>
                    이전
                </button>
                <span>
                    {query.page} / {pages} · {count}명
                </span>
                <button type="button" disabled={query.page >= pages} onClick={() => setQuery({ ...query, page: query.page + 1 })}>
                    다음
                </button>
            </div>
            {member && (
                <section>
                    <div className="ai-actions">
                        <h3>
                            {member.nick_name || member.name} · 회원 {member.id}
                        </h3>
                        <button type="button" onClick={close}>
                            기록 닫기
                        </button>
                        <button type="button" onClick={() => select(member)}>
                            기록 새로고침
                        </button>
                    </div>
                    <p role="status">{detailNotice}</p>
                    {comparison && (
                        <>
                            <h4>최근 프로필 적용 전·후</h4>
                            <p>
                                변경 시각 {time(comparison.changedAt)} · 요청 {comparison.requestId || '-'}
                            </p>
                            <div className="ai-photos">
                                {[
                                    ['previous', '이전 사진'],
                                    ['after', '적용 후 사진'],
                                ].map(([side, label]) => (
                                    <MemberImage key={side} path={`/adminUser/${member.id}/aiProfile/comparison`} side={side} initial={comparison[side]} label={label} />
                                ))}
                            </div>
                        </>
                    )}
                    {!history.length && !detailNotice && <p>AI 프로필 생성 기록이 없어요.</p>}
                    {history.map((row) => (
                        <article key={row.id}>
                            <h4>
                                {status(row.status)} · {row.id}
                            </h4>
                            <p>
                                접수 {time(row.createdAt)} · 완료 {time(row.completedAt)} · 실패 {time(row.failedAt)}
                            </p>
                            <dl className="ai-details">
                                {[
                                    ['회원 정보 원본 닉네임', row.memberSnapshot?.nickname || '-'],
                                    ['회원 정보 원본 성별', row.memberSnapshot?.gender || '-'],
                                    ['회원 정보 원본 출생 연도', row.memberSnapshot?.birth || '-'],
                                    ['회원 정보 원본 관심사', row.memberSnapshot?.interests?.join(', ') || '-'],
                                    ['입력 성별·출처', selectedInput(row, 'gender')],
                                    ['입력 출생 연도·출처', selectedInput(row, 'birth')],
                                    ['입력 취미·출처', selectedInput(row, 'hobby')],
                                    ['입력 종류·출처', selectedInput(row, 'style')],
                                    ['사진 입력', inputOrigins[row.inputSource?.photo] || '-'],
                                    ['진입', row.entrySource === 'APP' ? '앱' : '웹'],
                                    ['동의', row.consent?.agreed ? '동의함' : '미확인'],
                                    ['동의 시각', time(row.consent?.agreedAt)],
                                    ['약관 버전', row.consent?.version || '-'],
                                    ['프로필 적용', { APPLIED: '적용됨', UNAPPLIED: '미적용' }[row.application?.status] || row.application?.status || '미적용'],
                                    ['적용 시각', time(row.application?.appliedAt)],
                                    ['사진 저장 시도', time(row.activities?.downloadedAt)],
                                    ['공유 시도', time(row.activities?.sharedAt)],
                                    ['다시 만들기', time(row.activities?.retriedAt)],
                                ].map(([label, value]) => (
                                    <React.Fragment key={label}>
                                        <dt>{label}</dt>
                                        <dd>{value || '-'}</dd>
                                    </React.Fragment>
                                ))}
                            </dl>
                            {row.error && (
                                <p className="ai-notice" role="alert">
                                    생성 오류: {row.error}
                                </p>
                            )}
                            <div className="ai-photos">
                                {[
                                    ['original', '원본'],
                                    ['ai', 'AI 결과'],
                                    ['final', '완성 사진'],
                                ].map(([kind, label]) =>
                                    row[kind + 'Key'] ? (
                                        <MemberImage key={kind} path={`/adminUser/${member.id}/aiProfile/requests/${encodeURIComponent(row.id)}/image?kind=${kind}`} label={label} />
                                    ) : (
                                        <p key={kind}>{label}: 사진 없음</p>
                                    ),
                                )}
                            </div>
                        </article>
                    ))}
                </section>
            )}
        </section>
    );
}
